import { useCallback, useEffect, useRef, useState } from "react"

import type { History } from "@/types"
import {
  historyResponseToHistory,
  useGetHistoriesApi,
} from "@/hooks/api/histories"

const PAGE_SIZE = 20
type RequestMode = "initial" | "more" | "refresh"

interface ActiveRequest {
  controller: AbortController
  mode: RequestMode
}

const sortByUpdatedAt = (histories: History[]) =>
  [...histories].sort((left, right) =>
    (right.updatedAt || right.createdAt).localeCompare(
      left.updatedAt || left.createdAt
    )
  )

const mergeHistories = (current: History[], incoming: History[]) => {
  const byId = new Map(current.map((history) => [history.id, history]))
  incoming.forEach((history) => byId.set(history.id, history))
  return sortByUpdatedAt([...byId.values()])
}

export const useAgentTestHistories = (
  workspaceId?: string,
  agentId?: string
) => {
  const requestedScope = `${workspaceId ?? ""}:${agentId ?? ""}`
  const { fetchFn } = useGetHistoriesApi()
  const [rows, setRows] = useState<History[]>([])
  const [selectedHistory, setSelectedHistory] = useState<History>()
  const [total, setTotal] = useState(0)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [initialError, setInitialError] = useState<unknown>()
  const [loadMoreError, setLoadMoreError] = useState<unknown>()
  const [refreshError, setRefreshError] = useState<unknown>()
  const [stateScope, setStateScope] = useState(requestedScope)
  const generationRef = useRef(0)
  const activeRequestRef = useRef<ActiveRequest | undefined>(undefined)

  useEffect(() => {
    generationRef.current += 1
    const activeRequest = activeRequestRef.current
    activeRequestRef.current = undefined
    activeRequest?.controller.abort()
    setRows([])
    setSelectedHistory(undefined)
    setTotal(0)
    setHasLoaded(false)
    setIsInitialLoading(false)
    setIsLoadingMore(false)
    setInitialError(undefined)
    setLoadMoreError(undefined)
    setRefreshError(undefined)
    setStateScope(requestedScope)
  }, [agentId, requestedScope, workspaceId])

  const fetchPage = useCallback(
    async (offset: number, mode: RequestMode) => {
      if (!workspaceId || !agentId) return false

      const previousRequest = activeRequestRef.current
      activeRequestRef.current = undefined
      if (previousRequest) {
        previousRequest.controller.abort()
        if (previousRequest.mode === "more") setIsLoadingMore(false)
        if (previousRequest.mode === "initial") setIsInitialLoading(false)
      }

      const controller = new AbortController()
      const request = { controller, mode }
      activeRequestRef.current = request
      const generation = generationRef.current
      const isCurrent = () =>
        activeRequestRef.current === request &&
        generationRef.current === generation &&
        !controller.signal.aborted

      if (mode === "more") {
        setIsLoadingMore(true)
        setLoadMoreError(undefined)
      } else if (mode === "initial") {
        setIsInitialLoading(true)
        setInitialError(undefined)
      } else {
        setRefreshError(undefined)
      }

      try {
        const searchParams = new URLSearchParams({
          workspace_id: workspaceId,
          surface: "agent_live_test",
          agent_id: agentId,
          order_by: "-updated_at",
          limit: String(PAGE_SIZE),
          offset: String(offset),
        })
        const response = await fetchFn({
          searchParams,
          signal: controller.signal,
          preserveData: true,
        })
        if (!isCurrent() || !response?.data) return false

        const nextRows = response.data.map(historyResponseToHistory)
        setRows((current) =>
          offset === 0
            ? mergeHistories([], nextRows)
            : mergeHistories(current, nextRows)
        )
        setTotal(response.pagination?.total_records ?? nextRows.length)
        setSelectedHistory((current) => {
          if (!current) return current
          return nextRows.find((row) => row.id === current.id) ?? current
        })
        setHasLoaded(true)
        return true
      } catch (error) {
        if (!isCurrent()) return false
        if (mode === "more") setLoadMoreError(error)
        else if (mode === "initial") setInitialError(error)
        else setRefreshError(error)
        return false
      } finally {
        if (activeRequestRef.current === request) {
          activeRequestRef.current = undefined
          if (mode === "more") setIsLoadingMore(false)
          if (mode === "initial") setIsInitialLoading(false)
        }
      }
    },
    [agentId, fetchFn, workspaceId]
  )

  const ensureFirstPage = useCallback(() => {
    if (hasLoaded || isInitialLoading) return Promise.resolve(true)
    return fetchPage(0, "initial")
  }, [fetchPage, hasLoaded, isInitialLoading])

  const loadMore = useCallback(
    () => fetchPage(rows.length, "more"),
    [fetchPage, rows.length]
  )
  const refreshFirstPage = useCallback(
    () => fetchPage(0, "refresh"),
    [fetchPage]
  )

  const selectHistory = useCallback((history?: History) => {
    setSelectedHistory(history)
  }, [])

  const upsertHistory = useCallback(
    (history: History) => {
      const exists = rows.some((item) => item.id === history.id)
      setRows((current) => mergeHistories(current, [history]))
      if (!exists) setTotal((current) => current + 1)
      setSelectedHistory((current) =>
        current?.id === history.id ? history : current
      )
    },
    [rows]
  )

  const patchHistory = useCallback(
    (historyId: number, patch: Partial<History>) => {
      setRows((current) =>
        sortByUpdatedAt(
          current.map((history) =>
            history.id === historyId ? { ...history, ...patch } : history
          )
        )
      )
      setSelectedHistory((current) =>
        current?.id === historyId ? { ...current, ...patch } : current
      )
    },
    []
  )

  const removeHistory = useCallback((historyId: number) => {
    setRows((current) => current.filter((history) => history.id !== historyId))
    setTotal((current) => Math.max(0, current - 1))
    setSelectedHistory((current) =>
      current?.id === historyId ? undefined : current
    )
  }, [])

  const isCurrentScope = stateScope === requestedScope
  const visibleRows = isCurrentScope ? rows : []

  return {
    rows: visibleRows,
    selectedHistory: isCurrentScope ? selectedHistory : undefined,
    total: isCurrentScope ? total : 0,
    hasMore: isCurrentScope && visibleRows.length < total,
    hasLoaded: isCurrentScope && hasLoaded,
    isInitialLoading: isCurrentScope && isInitialLoading,
    isLoadingMore: isCurrentScope && isLoadingMore,
    initialError: isCurrentScope ? initialError : undefined,
    loadMoreError: isCurrentScope ? loadMoreError : undefined,
    refreshError: isCurrentScope ? refreshError : undefined,
    ensureFirstPage,
    loadMore,
    retryInitial: () => fetchPage(0, "initial"),
    retryLoadMore: loadMore,
    refreshFirstPage,
    selectHistory,
    upsertHistory,
    patchHistory,
    removeHistory,
  }
}

export type AgentTestHistoriesState = ReturnType<typeof useAgentTestHistories>
